import React, { useEffect, useState } from "react";
import { Row, Col, Spin, Carousel } from "antd";
import CarouselComponent from "components/hompage/Carousel";
import { useAuth } from "../contexts/AuthContext";
import CourseCard from "../components/hompage/CourseCard";
import CateCard from "../components/hompage/CategoryCard";
import CourseSuggestionChatbot from "../components/CourseSuggestionChatbot";
import LearnerCourseService from "../services/LearnerCourseService";
import LearnerCategoryService from "../services/LearnerCategoryService";
import LearnerApiService from "../services/LearnerApiService";
import CartApiService from "../services/CartApiService";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import LazyLoad from 'react-lazyload';

const HomePage = () => {
  const { authenticated, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [newCourses, setNewCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryTotalPages, setCategoryTotalPages] = useState(1);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const pageSizeCategory = 10;
  const [error, setError] = useState(null);
  const [cartCourseSlugs, setCartCourseSlugs] = useState([]);
  const navigate = useNavigate();

  const fetchCategories = async (page = 1) => {
    setCategoryLoading(true);
    try {
      const res = await LearnerCategoryService.getLearnerCategories({
        page,
        limit: pageSizeCategory,
      });
      setCategories(res.categories);
      setCategoryTotalPages(res.pagination.totalPages || 1);
      setCategoryLoading(false);
    } catch (err) {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const topSellingCourses = await LearnerCourseService.getLearnerCourses({
          sort: '-purchaseCount',
          pageSize: 10
        });
        setCourses(topSellingCourses.courses);

        const newData = await LearnerCourseService.getLearnerCourses({
          sort: '-createdAt',
          pageSize: 10,
        });
        setNewCourses(newData.courses);

        fetchCategories(1);
        // setLoading(false);
      } catch (error) {
        console.error("Error fetching courses:", error);
        setError("Failed to load courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCourses = (course) => ({
    title: course.name,
    image: course.imageUrl,
    author: course.author,
    description: course.description,
    price: course.price === 0 ? "Free" : course.price,
    totalSections: course.totalSections,
    category: course.category?.name || "General",
    courseId: course.id || course._id,
    isPurchased: !!course.bought,
  });
  const formatCategories = (categories) => ({
    name: categories.name,
    totalCourses: categories.totalCourses,
  });

  const refetchCart = async () => {
    try {
      const res = await CartApiService.getCart();
      if (res.success && res.data && Array.isArray(res.data.courses)) {
        setCartCourseSlugs(res.data.courses.map(c => c.slug));
      } else {
        setCartCourseSlugs([]);
      }
    } catch (e) {
      setCartCourseSlugs([]);
    }
  };

  useEffect(() => {
    refetchCart();
    const handleCartChanged = () => refetchCart();
    window.addEventListener('cart-changed', handleCartChanged);
    return () => window.removeEventListener('cart-changed', handleCartChanged);
  }, []);

  return (
    <div className="homepage-container">
      <div className="carousel-container-homepage">
        <CarouselComponent />
      </div>

      <div className="courses-category-homepage" style={{position: 'relative'}}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: "8px" }}
        >
          <Col>
            <h2 style={{ marginBottom: 0 }}>Top Categories</h2>
          </Col>
        </Row>
        <p>Explore our Popular Categories</p>
        <div style={{ position: "relative" }}>
          {categoryLoading && categories.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "150px",
              }}
            >
              <Spin />
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              {categoryLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.5)",
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spin />
                </div>
              )}
              <button
                className="carousel-arrow left"
                style={{
                  position: "absolute",
                  left: -32,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                }}
                disabled={categoryPage === 1 || categoryLoading}
                onClick={() => {
                  if (categoryPage > 1) {
                    setCategoryPage(categoryPage - 1);
                    fetchCategories(categoryPage - 1);
                  }
                }}
              >
                <LeftOutlined />
              </button>
              <Row gutter={[16, 16]} className="category-row-homepage">
                {categories.map((category) => (
                  <Col key={category.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                    <LazyLoad height={120} offset={100} once>
                      <CateCard
                        {...formatCategories(category)}
                        onClick={() => navigate(`/all-courses?category=${category.slug}`)}
                      />
                    </LazyLoad>
                  </Col>
                ))}
              </Row>
              <button
                className="carousel-arrow right"
                style={{
                  position: "absolute",
                  right: -32,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                }}
                disabled={
                  categoryPage === categoryTotalPages || categoryLoading
                }
                onClick={() => {
                  if (categoryPage < categoryTotalPages) {
                    setCategoryPage(categoryPage + 1);
                    fetchCategories(categoryPage + 1);
                  }
                }}
              >
                <RightOutlined />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="courses-feature-homepage">
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: "8px" }}
        >
          <Col>
            <h2 style={{ marginBottom: 0 }}>Top Selling Courses</h2>
          </Col>
          <Col>
            <button
              onClick={() => navigate("/all-courses")}
              className="all-courses-btn"
            >
              All Courses
            </button>
          </Col>
        </Row>
        <p>Explore our Popular Courses</p>
        {loading ? (
          <Spin />
        ) : (
          <div style={{ position: "relative", width: "100%" }}>
            <button
              className="carousel-arrow left"
              style={{ left: "calc(50% - 700px)", top: "40%", zIndex: 2, position: "absolute" }}
              onClick={() => window.topSellingCoursesCarousel && window.topSellingCoursesCarousel.prev()}
            >
              <LeftOutlined />
            </button>
            <div className="top-selling-courses-carousel-wrapper">
              <Carousel
                slidesToShow={4}
                slidesToScroll={1}
                dots={false}
                arrows={false}
                ref={c => (window.topSellingCoursesCarousel = c)}
                responsive={[
                  { breakpoint: 1200, settings: { slidesToShow: 3 } },
                  { breakpoint: 900, settings: { slidesToShow: 2 } },
                  { breakpoint: 600, settings: { slidesToShow: 1 } },
                ]}
              >
                {courses.map((course) => (
                  <div key={course.id} className="top-selling-courses-carousel-slide">
                    <LazyLoad height={300} offset={100} once>
                    <CourseCard {...formatCourses(course)} slug={course.slug} isInCart={cartCourseSlugs.includes(course.slug)} />
                    </LazyLoad>
                  </div>
                ))}
              </Carousel>
            </div>
            <button
              className="carousel-arrow right"
              style={{ right: "calc(50% - 700px)", top: "40%", zIndex: 2, position: "absolute" }}
              onClick={() => window.topSellingCoursesCarousel && window.topSellingCoursesCarousel.next()}
            >
              <RightOutlined />
            </button>
          </div>
        )}
      </div>

      <div className="courses-feature-homepage">
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: "8px" }}
        >
          <Col>
            <h2 style={{ marginBottom: 0 }}>New Courses</h2>
          </Col>
        </Row>
        <p>Check out the latest courses just released!</p>
        {loading ? (
          <Spin />
        ) : (
          <div style={{ position: "relative", width: "100%" }}>
            <button
              className="carousel-arrow left"
              style={{ left: "calc(50% - 700px)", top: "40%", zIndex: 2, position: "absolute" }}
              onClick={() => window.newCoursesCarousel && window.newCoursesCarousel.prev()}
            >
              <LeftOutlined />
            </button>
            <div className="new-courses-carousel-wrapper">
              <Carousel
                slidesToShow={4}
                slidesToScroll={1}
                dots={false}
                arrows={false}
                ref={c => (window.newCoursesCarousel = c)}
                responsive={[
                  { breakpoint: 1200, settings: { slidesToShow: 3 } },
                  { breakpoint: 900, settings: { slidesToShow: 2 } },
                  { breakpoint: 600, settings: { slidesToShow: 1 } },
                ]}
              >
                {newCourses.map((course) => (
                  <div key={course.id} className="new-courses-carousel-slide">
                    <LazyLoad height={300} offset={100} once>
                    <CourseCard {...formatCourses(course)} slug={course.slug} isInCart={cartCourseSlugs.includes(course.slug)} />
                    </LazyLoad>
                  </div>
                ))}
              </Carousel>
            </div>
            <button
              className="carousel-arrow right"
              style={{ right: "calc(50% - 700px)", top: "40%", zIndex: 2, position: "absolute" }}
              onClick={() => window.newCoursesCarousel && window.newCoursesCarousel.next()}
            >
              <RightOutlined />
            </button>
          </div>
        )}      </div>
      
      {/* Course Suggestion AI Chatbot */}
      <CourseSuggestionChatbot />
    </div>
  );
};
export default HomePage;
